import { CheckCircleFilled, SearchOutlined, VideoCameraOutlined } from '@ant-design/icons'
import { Button, Empty, Input, Modal, Pagination, Spin } from 'antd'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { listPickableVideos, videoGenTaskPosterUrl, type PickableVideo } from '../../api'
import './video-picker-modal.css'

const PAGE_SIZE = 12
const SEARCH_DEBOUNCE_MS = 300

function formatDate(iso: string | null, locale: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString(locale, { hour12: false })
}

export interface VideoPickerModalProps {
  open: boolean
  workspaceId: string
  /** Excludes a video from the pickable list — e.g. a video already
   * selected as the current continuation source, or the anchor itself
   * when re-picking (spec §3 never lets a video continue "from itself"). */
  excludeVideoId?: string | null
  title?: string
  onCancel: () => void
  onConfirm: (video: PickableVideo) => void
}

/** Video Picker (spec §3) — "已经生成完成的视频" only (the backend's
 * `pickable-videos` endpoint already filters to `succeeded` + a real
 * output row; nothing queued/running/failed is ever returned here).
 * Deliberately its own light-weight component rather than reusing
 * `ImageAssetPicker`'s heavier asset-library shell (different domain
 * object — a video task, not a site image asset — and this only needs
 * search + pagination + single-select, not upload/external-link tabs). */
export default function VideoPickerModal({
  open,
  workspaceId,
  excludeVideoId,
  title,
  onCancel,
  onConfirm,
}: VideoPickerModalProps) {
  const { t, i18n } = useTranslation(['video', 'actions'])
  const modalTitle = title ?? t('video:picker.defaultTitle')
  const dateLocale = i18n.language?.startsWith('zh') ? 'zh-CN' : 'en-US'
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [page, setPage] = useState(1)
  const [items, setItems] = useState<PickableVideo[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!open) return
    setSearch('')
    setDebouncedSearch('')
    setPage(1)
    setSelectedId(null)
  }, [open])

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(search.trim())
      setPage(1)
    }, SEARCH_DEBOUNCE_MS)
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
    }
  }, [search])

  useEffect(() => {
    if (!open || !workspaceId) return
    let cancelled = false
    setLoading(true)
    listPickableVideos(workspaceId, {
      search: debouncedSearch || undefined,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    })
      .then(({ data }) => {
        if (cancelled) return
        setItems(data.items)
        setTotal(data.total)
      })
      .catch(() => {
        if (cancelled) return
        setItems([])
        setTotal(0)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, workspaceId, debouncedSearch, page])

  const visibleItems = useMemo(
    () => (excludeVideoId ? items.filter((it) => it.id !== excludeVideoId) : items),
    [items, excludeVideoId],
  )

  const selected = visibleItems.find((it) => it.id === selectedId) ?? null

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title={modalTitle}
      width={840}
      footer={null}
      destroyOnClose
      rootClassName="tc-video-picker-modal"
    >
      <div className="tc-video-picker">
        <div className="tc-video-picker-toolbar">
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder={t('video:picker.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 260 }}
          />
        </div>
        <div className="tc-video-picker-body">
          {loading ? (
            <div className="tc-video-picker-loading">
              <Spin />
            </div>
          ) : visibleItems.length === 0 ? (
            <Empty description={t('video:picker.empty')} className="tc-video-picker-empty" />
          ) : (
            <div className="tc-video-picker-grid">
              {visibleItems.map((item) => {
                const isSelected = item.id === selectedId
                return (
                  <div
                    key={item.id}
                    className={`tc-video-picker-card${isSelected ? ' is-selected' : ''}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedId(item.id)}
                    onDoubleClick={() => onConfirm(item)}
                  >
                    <div className="tc-video-picker-thumb">
                      <PosterImg src={videoGenTaskPosterUrl(workspaceId, item.id)} />
                      {isSelected && <CheckCircleFilled className="tc-video-picker-check" />}
                    </div>
                    <div className="tc-video-picker-meta">
                      <div className="tc-video-picker-prompt" title={item.prompt || ''}>
                        {item.prompt || t('video:picker.noPrompt')}
                      </div>
                      <div className="tc-video-picker-params">
                        {item.duration_seconds != null ? t('video:picker.durationSeconds', { seconds: item.duration_seconds }) : '—'} ·{' '}
                        {item.quality_tier || '—'} · {item.aspect_ratio || '—'}
                      </div>
                      <div className="tc-video-picker-time">{formatDate(item.finished_at || item.created_at, dateLocale)}</div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
        <div className="tc-video-picker-footer">
          <Pagination
            current={page}
            pageSize={PAGE_SIZE}
            total={total}
            size="small"
            showSizeChanger={false}
            onChange={setPage}
          />
          <div className="tc-video-picker-footer-actions">
            <Button onClick={onCancel}>{t('actions:cancel')}</Button>
            <Button type="primary" disabled={!selected} onClick={() => selected && onConfirm(selected)}>
              {t('video:picker.confirm')}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

function PosterImg({ src }: { src: string }) {
  const [errored, setErrored] = useState(false)
  if (errored) {
    return (
      <div className="tc-video-picker-thumb-fallback">
        <VideoCameraOutlined />
      </div>
    )
  }
  return <img src={src} alt="" onError={() => setErrored(true)} />
}
